import { DateRange, GeneralLedgerFilters } from 'general-ledger/types';
import * as React from 'react';
import {
  PageWrapper,
  PageNavigation,
  PageBody,
  TogglePageSidebarButtonWrapper,
  PageFooter,
} from './NewGeneralLedger.styles';
import { Button } from 'bench-internal-theme';
import LedgerSidebar from './components/ledger-sidebar/LedgerSidebar';
import { GeneralLedgerProvider } from './hooks/useGeneralLedger';
import { LedgerTable } from './components/ledger-table/LedgerTable';
import { GroupedVirtuosoHandle } from 'react-virtuoso';
import PageHeader from './components/page-header/PageHeader';
import { RightSidebars } from './components/right-sidebars/RightSidebars';
import { BulkEditFormContainer } from './components/bulk-edit/BulkEditFormContainer';
import { SelectedDataEntriesProvider } from './hooks/useSelectedEntries';
import { GeneralLedgerErrorBoundary } from './components/error-boundary/GeneralLedgerErrorBoundary';

type NewGeneralLedgerProps = {
  clientId: string;
  dateRange: DateRange;
  filters: GeneralLedgerFilters;
  updateQueryParams: (params: Record<string, string>) => void;
};

export const NewGeneralLedger = ({
  clientId,
  dateRange,
  filters,
  updateQueryParams,
}: NewGeneralLedgerProps): React.ReactElement => {
  const [isNavigationExpanded, setIsNavigationExpanded] = React.useState(true);
  const ledgerTableVirtuosoRef = React.useRef<GroupedVirtuosoHandle>(null);

  const onLedgerSidebarItemClick = (index: number) => {
    if (ledgerTableVirtuosoRef.current) {
      ledgerTableVirtuosoRef.current.scrollToIndex({ index });
    }
  };

  const onSearchUpdate = (search?: string) => {
    if (ledgerTableVirtuosoRef.current && search) {
      // Scroll to the top of the table when the search is updated
      ledgerTableVirtuosoRef.current.scrollToIndex({ index: 0 });
    }
  };

  return (
    <GeneralLedgerErrorBoundary>
      <GeneralLedgerProvider
        clientId={clientId}
        initialState={{
          dateRange,
          showAccounts: !!filters.showAccounts,
          search: filters.search,
        }}
      >
        <PageWrapper isNavigationExpanded={isNavigationExpanded}>
          <PageHeader updateQueryParams={updateQueryParams} onSearchUpdate={onSearchUpdate} />
          <PageNavigation>
            <TogglePageSidebarButtonWrapper>
              <Button
                data-heap-id={`general-ledger-${
                  isNavigationExpanded ? 'hide' : 'show'
                }-sidebar-button`}
                icon={isNavigationExpanded ? 'doubleCaretLeft' : 'doubleCaretRight'}
                size="small"
                styleType="primary"
                onClick={() => setIsNavigationExpanded(!isNavigationExpanded)}
              />
            </TogglePageSidebarButtonWrapper>
            {isNavigationExpanded ? <LedgerSidebar onItemClick={onLedgerSidebarItemClick} /> : null}
          </PageNavigation>
          <SelectedDataEntriesProvider>
            <PageBody>
              <LedgerTable ref={ledgerTableVirtuosoRef} />
            </PageBody>
            <PageFooter>
              <BulkEditFormContainer />
            </PageFooter>
          </SelectedDataEntriesProvider>
        </PageWrapper>
        <RightSidebars />
      </GeneralLedgerProvider>
    </GeneralLedgerErrorBoundary>
  );
};
